import { getVodService } from '@/shared/getContainer/getVodService'
import { useState } from 'react'
import { EpisodeItemInterface } from '@/types/vod/VodItemDetails'
import { useAppStore, useVODStore } from '@/shared/store'
import { fetchItemsAmount } from '@/shared/constants'
import {
    selectSetActualEpisode,
    selectSetItemDetails,
    selectSetSelectedVOD,
    selectSetVodPages,
} from '@/shared/store/storeSelectors/vodSelectors'
import { getErrorMessage } from '@/shared/lib'
import { useVODService } from '@/app/context/DIContext/DIServices'
import { PlaybackType } from '@/types/PlaybackType/PlaybackType'
import assetsTypes from '@/types/assetsTypes/assetsTypes'
import { IVODServiceToya } from '@/shared/types/services/types/vodService/vodServiceInterfaces'

type props = {
    episodes: EpisodeItemInterface[] | undefined
    actualSeasonId: string
    getItemDetails?: (
        item: {
            productId: string | null
            assetType: string | undefined
            id: string
        },
        value?: boolean | undefined
    ) => void
}
const { getItems } = (await getVodService()) as IVODServiceToya
export const useEpisodesSliderHook = ({
    getItemDetails,
    episodes,
    actualSeasonId,
}: props) => {
    const setItemDetails = useVODStore(selectSetItemDetails)
    const setVodPages = useVODStore(selectSetVodPages)
    const setSelectedVOD = useVODStore(selectSetSelectedVOD)
    const setActualEpisode = useVODStore(selectSetActualEpisode)
    const { setAssetType } = useAppStore()
    const [episodeList, setEpisodeList] = useState<EpisodeItemInterface[]>(
        episodes || []
    )
    const [fetchAmount, setFetchAmount] = useState(fetchItemsAmount)
    const [isFetchedAll, setIsFetchedAll] = useState(false)
    const [selectedEpisode, setSelectedEpisode] =
        useState<EpisodeItemInterface>()

    const { getPlaybacks } = useVODService()

    const setVodItem = (
        getPlayback: PlaybackType,
        episode: EpisodeItemInterface
    ) => {
        const { productType } = episode

        if (getPlayback && productType) {
            setAssetType(productType.toUpperCase() as assetsTypes)

            const {
                drmId,
                preAuthTokenUri,
                licenseProxyUri,
                drmType,
                certificateUri,
            } = getPlayback.drmInfo

            setSelectedVOD({
                drmId: drmId || '',
                drmType: drmType || '',
                preAuthTokenUri: preAuthTokenUri || '',
                licenseProxyUri: licenseProxyUri || '',
                playbackPositionUpdateInterval:
                    getPlayback.playbackPositionUpdateInterval,
                position: getPlayback.position,
                uri: getPlayback.uri,
                certificateUri,
            })
        }
    }

    const loadContent = async (episode: EpisodeItemInterface) => {
        const { productId, productType, serviceName } = episode

        try {
            if (productId && productType) {
                const getPlayback = await getPlaybacks(
                    productId,
                    productType,
                    serviceName
                )
                if (getPlayback) {
                    setVodItem(getPlayback, episode)
                }
            }
        } catch (error) {
            getErrorMessage('DetailsButton - load content', error)
            throw error
        }
    }

    const handleItemClick = (episode: EpisodeItemInterface) => {
        getItemDetails?.({
            productId: episode.productId,
            assetType: episode.productType,
            id: episode.productId,
        })
        setItemDetails({
            productId: episode.productId,
            assetType: episode.productType,
            id: episode.productId,
            serviceName: 'vod-service',
        })
        setVodPages(
            episode.productId,
            episode.title,
            episode.productType as 'episode'
        )

        setActualEpisode(episode)

        loadContent(episode)
    }

    const fetchMoreEpisodes = async () => {
        if (isFetchedAll) return

        const offset = episodeList.length
        const newEpisodes = await getItems<EpisodeItemInterface>(
            actualSeasonId,
            offset,
            fetchAmount
        )
        setFetchAmount(newEpisodes.length)
        setIsFetchedAll(newEpisodes.length < fetchAmount)

        const updatedEpisodeList = [...episodeList]

        newEpisodes.forEach((newEpisode) => {
            if (
                !updatedEpisodeList.some(
                    (episode) => episode.productId === newEpisode.productId
                )
            ) {
                updatedEpisodeList.push(newEpisode)
            }
        })

        setEpisodeList(updatedEpisodeList)
    }

    return {
        selectedEpisode,
        setSelectedEpisode,
        fetchMoreEpisodes,
        handleItemClick,
        setEpisodeList,
        episodeList,
    }
}
